// Library methods
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import TableSkeleton from "src/components/containers/shared/TableSkeleton/TableSkeleton";
import CustomPagination from "src/components/shared/pagination/CustomPagination/CustomPagination";
import TableHeader from "src/components/shared/tables/TableHeader/TableHeader";
import RecentExamCard from "./RecentExamCard/RecentExamCard";

// Utils
import {
  getItemsToRender,
  getPageCount,
} from "src/utils/pagination/paginationHelper";
import { multiKeyCompare } from "src/utils/tables/tableHelper";
import { getUtcStartEndDate } from "src/utils/date/dateHelper";
import { getExamCardText } from "src/utils/exams/examReportsHelper";

// Types
import { OrderType } from "src/types/sharedTypes";
import { ExamType } from "src/services/exams/types";

// Hooks
import { useRecentlyCompletedExamsData } from "src/services/exams/hooks/dashboard-exams/useRecentlyCompletedExamsData";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Styles
import { useTheme } from "@mui/material/styles";

const LOADING_NUM_OF_ROWS = 5;
const MAX_NUM_OF_ROWS = 10;

type ExtendedExamType = ExamType & {
  firstName: string;
  lastName: string;
  examName: string;
};

type RecentExamsType = {
  timeRange: number;
  searchTerm: string;
  onLoadingChange?: (isLoading: boolean) => void;
  forceExpandKey?: number;
};

const RecentExams = ({
  timeRange,
  searchTerm,
  onLoadingChange,
  forceExpandKey,
}: RecentExamsType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const theme = useTheme();

  const recentFilterPayload = useMemo(
    () => ({
      recentFilterDate: getUtcStartEndDate(timeRange, clinicInfo?.timezone)
        .startOfUTC,
    }),
    [timeRange, clinicInfo?.timezone]
  );

  const {
    data: recentExamsData,
    isLoading: isLoadingRecentExams,
    isError: isErrorRecentExams,
  } = useRecentlyCompletedExamsData(clinicInfo?.id, recentFilterPayload);

  const formattedExams: Array<ExtendedExamType> = useMemo(
    () =>
      (recentExamsData ?? [])?.map((exam) => ({
        ...exam,
        firstName: exam.patient.firstName,
        lastName: exam.patient.lastName,
        examName: getExamCardText({ exam, t }),
      })),
    [t, recentExamsData]
  );

  const hasRecentExams = useMemo(
    () =>
      !isLoadingRecentExams &&
      !isErrorRecentExams &&
      formattedExams &&
      formattedExams?.length > 0,
    [isLoadingRecentExams, isErrorRecentExams, formattedExams]
  );

  const hasNoRecentExams = useMemo(
    () =>
      !isLoadingRecentExams && (isErrorRecentExams || !formattedExams?.length),
    [isLoadingRecentExams, isErrorRecentExams, formattedExams]
  );

  const orderByCompletedDate: Array<keyof ExtendedExamType> = [
    "completionDate",
  ];
  const orderByPatientName: Array<keyof ExtendedExamType> = [
    "firstName",
    "lastName",
  ];
  const orderByExamName: Array<keyof ExtendedExamType> = ["examName"];

  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(true);

  const [order, setOrder] = useState<OrderType>("desc");

  const [orderByOptions, setOrderByOptions] = useState<
    Array<keyof ExtendedExamType>
  >([...orderByCompletedDate, ...orderByPatientName]);

  const headCells = [
    {
      id: "patient",
      label: t("Patient"),
      gridSize: 3.35,
      orderUsing: [
        ...orderByPatientName,
        ...orderByCompletedDate,
        ...orderByExamName,
      ],
    },
    {
      id: "type",
      label: t("Type"),
      gridSize: 4.15,
      orderUsing: [
        ...orderByExamName,
        ...orderByPatientName,
        ...orderByCompletedDate,
      ],
    },
    {
      id: "completion-date",
      label: t("Completed"),
      gridSize: 4.5,
      orderUsing: [
        ...orderByCompletedDate,
        ...orderByPatientName,
        ...orderByExamName,
      ],
    },
  ];

  const handleRequestSort = useCallback(
    (
      _: React.MouseEvent<HTMLButtonElement>,
      property: Array<keyof ExtendedExamType>
    ) => {
      const isAsc =
        orderByOptions.length &&
        orderByOptions[0] === property[0] &&
        order === "asc";

      setOrder(isAsc ? "desc" : "asc");
      setOrderByOptions(property);
    },
    [order, orderByOptions]
  );

  // Filtered rows
  const filteredRows = useMemo(() => {
    let filterArray = formattedExams?.filter((exam) => {
      const debouncedLowerCase = searchTerm?.toLowerCase();

      const searchTermHasName = `${exam?.firstName} ${exam?.lastName}`
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      const searchTermHasExamName = exam?.examName
        ?.toString()
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      const searchTermHasCompletionDate = exam?.completionDate
        ?.toString()
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      return (
        searchTermHasName ||
        searchTermHasExamName ||
        searchTermHasCompletionDate
      );
    });

    return multiKeyCompare<ExtendedExamType>(
      filterArray,
      order === "asc",
      orderByOptions
    );
  }, [formattedExams, order, orderByOptions, searchTerm]);

  const hasNoSearchResults = useMemo(
    () => searchTerm?.length > 0 && !filteredRows?.length,
    [searchTerm, filteredRows]
  );

  // exams to render
  const examsOnCurrentPage = useMemo(() => {
    return getItemsToRender(filteredRows, page, MAX_NUM_OF_ROWS);
  }, [filteredRows, page]);

  // Pagination change
  const onPaginationChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    },
    [setPage]
  );

  useEffect(() => setPage(1), [filteredRows]);

  useEffect(() => {
    onLoadingChange?.(isLoadingRecentExams);
  }, [isLoadingRecentExams, onLoadingChange]);

  useEffect(() => {
    if (forceExpandKey) setExpanded(true);
  }, [forceExpandKey]);

  return (
    <Box
      display="flex"
      width="100%"
      height={expanded ? "100%" : "auto"}
      flexDirection="column"
      sx={{
        backgroundColor:
          theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20",
        borderRadius: "4px",
        p: "8px",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
      >
        <Typography
          alignContent="center"
          fontWeight={600}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
          maxWidth="100%"
        >
          {t("Recent Exams")}
        </Typography>

        <Button
          onClick={() => setExpanded((prev) => !prev)}
          size="small"
          sx={{
            color: "inherit",
            textTransform: "none",
            textDecoration: "underline",
          }}
        >
          {expanded ? t("Collapse") : t("Expand")}
        </Button>
      </Box>

      <Collapse in={expanded} timeout="auto">
        {isLoadingRecentExams && (
          <TableSkeleton numOfRows={LOADING_NUM_OF_ROWS} />
        )}

        {hasRecentExams && (
          <Box display="flex" flexDirection="column" mt="8px" gap="12px">
            <TableHeader<ExtendedExamType>
              headCells={headCells}
              order={order}
              orderBy={orderByOptions?.[0] ?? "completionDate"}
              onRequestSort={handleRequestSort}
              rowCount={examsOnCurrentPage.length}
              numSelected={0}
            />
            {examsOnCurrentPage?.map((exam) => (
              <RecentExamCard key={exam.id} exam={exam} />
            ))}
            <Box mt="12px">
              <CustomPagination
                page={page}
                count={getPageCount(filteredRows?.length, MAX_NUM_OF_ROWS)}
                onChange={onPaginationChange}
              />
            </Box>
          </Box>
        )}

        {/* Error messages */}
        {!hasNoRecentExams && hasNoSearchResults && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            my="60px"
          >
            <Typography textAlign="center" fontWeight={600}>
              {t("No Recently Completed Exams Found.")}
            </Typography>
            <Typography>
              {t("Change your search term to see more results.")}
            </Typography>
          </Box>
        )}
        {hasNoRecentExams && (
          <Box display="flex" justifyContent="center" my="60px">
            <Typography textAlign="center">
              {t("No Recently Completed Exams Found.")}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default RecentExams;
